import { Injectable } from '@angular/core';
import { CustomerStoreService } from '@app/ngrx-customer/services';
import { UserService } from '@app/user/services';
import { UserDataService } from '@app/user/services/user-data.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { OrderService } from '@order/services/order.service';
import { DSM_ACTIONS } from '@usf/ngrx-order';
import { DsmInfo } from '@usf/ngrx-order/lib/models/client/dsm-info';
import {
  catchError,
  concatMap,
  firstValueFrom,
  map,
  of,
  take,
  forkJoin,
  switchMap,
  combineLatest,
} from 'rxjs';
import { from } from 'rxjs';
import { PanAppState } from '@panamax/app-state';
import { FEATURES } from '@shared/constants/splitio-features';
import { OrderTracingService } from '../tracking/tracing/order-tracing.service';
import { ORDER_TRACING_ACTIONS } from '../tracking/tracing/order-tracing.actions';
import { dsmCustomersCallEndTrace } from '../tracking/tracing/trace-models/dsm-customers-call.trace';
import { genericTracingTransformer } from '../../shared/tracking/tracing/generic-tracing-transformer';

@Injectable({
  providedIn: 'root',
})
export class DsmClientEffects {
  getDsmInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DSM_ACTIONS.getDsmInfo),
      concatMap(() =>
        combineLatest([
          this.panAppState.feature$([FEATURES.split_dynamic_order_dsm.name]),
          this.panAppState.feature$([
            FEATURES.split_dynamic_payments_dsm_dashboard.name,
          ]),
        ]).pipe(
          take(1),
          concatMap(([dsmOrderEnabled, dsmPaymentsDashboardEnabled]) => {
            if (!dsmOrderEnabled && !dsmPaymentsDashboardEnabled) {
              return of([DSM_ACTIONS.getDsmInfoSuccess({ dsmInfo: [] })]);
            }
            return from(firstValueFrom(this.userService.isSuperUser$())).pipe(
              concatMap(isSuperUser => {
                if (isSuperUser) {
                  return of([DSM_ACTIONS.getDsmInfoSuccess({ dsmInfo: [] })]);
                }
                return this.orderService.isDsmUser().pipe(
                  take(1),
                  switchMap(isDsmUser => {
                    if (isDsmUser) {
                      this.orderTracingService.traceDsmCustomersCallStart();
                      // Get both DSM info and customers in parallel
                      return forkJoin({
                        dsmInfo: this.userDataService
                          .getDsmInfo()
                          .pipe(take(1)),
                        customersInState: this.customerStoreService
                          .loadCustomers$()
                          .pipe(take(1)),
                      }).pipe(
                        map(({ dsmInfo, customersInState }) => {
                          // Filter and enrich DSM users with customer details
                          const filteredDsmInfo: DsmInfo[] = dsmInfo
                            .map(dsmUser => ({
                              ...dsmUser,
                              customers: dsmUser.customers
                                .map(dsmCustomer => {
                                  const matchingCustomer =
                                    customersInState.find(
                                      customer =>
                                        customer.customerNumber ===
                                          dsmCustomer.customerNumber &&
                                        customer.divisionNumber ===
                                          dsmCustomer.divisionNumber,
                                    );

                                  if (matchingCustomer) {
                                    return {
                                      ...dsmCustomer,
                                      customerName:
                                        matchingCustomer.customerName,
                                      state: matchingCustomer.state,
                                      city: matchingCustomer.city,
                                      departments:
                                        matchingCustomer.departments?.map(
                                          dept => dept.departmentName,
                                        ) || [],
                                      departmentNumbers:
                                        matchingCustomer.departmentNumbers ||
                                        [],
                                    };
                                  }
                                  return null;
                                })
                                .filter(customer => customer !== null),
                            }))
                            .filter(dsmUser => dsmUser.customers?.length > 0);

                          const tracking = JSON.parse(
                            JSON.stringify(dsmCustomersCallEndTrace),
                          );
                          tracking.tracing.data.isStartOfTrace = false;
                          tracking.tracing.data.isEndOfTrace = true;
                          tracking.tracing.transformFunc =
                            genericTracingTransformer;

                          return [
                            DSM_ACTIONS.getDsmInfoSuccess({
                              dsmInfo: filteredDsmInfo,
                            }),
                            ORDER_TRACING_ACTIONS.traceDsmCustomersCallSuccess({
                              tracking,
                            }),
                          ];
                        }),
                        catchError((error: any) => {
                          const errorActions: any[] = [];
                          const tracking = JSON.parse(
                            JSON.stringify(dsmCustomersCallEndTrace),
                          );
                          tracking.tracing.data.isStartOfTrace = false;
                          tracking.tracing.data.isEndOfTrace = true;
                          tracking.tracing.transformFunc =
                            genericTracingTransformer;

                          errorActions.push(
                            DSM_ACTIONS.getDsmInfoFailure({ error }),
                            ORDER_TRACING_ACTIONS.traceDsmCustomersCallFail({
                              tracking,
                            }),
                          );
                          return of(errorActions);
                        }),
                      );
                    }
                    return of([DSM_ACTIONS.getDsmInfoSuccess({ dsmInfo: [] })]);
                  }),
                );
              }),
            );
          }),
        ),
      ),
      concatMap(actions => actions),
    ),
  );

  constructor(
    private actions$: Actions,
    private userDataService: UserDataService,
    private userService: UserService,
    private orderService: OrderService,
    private orderTracingService: OrderTracingService,
    private customerStoreService: CustomerStoreService,
    private panAppState: PanAppState,
  ) {}
}
